// lake-hexes.json: the LAKE hexes that are not a Natural Earth lake polygon (the Caspian flood
// fill, hexes promoted so that every lake owns one), each group with the sources its hexes cite.
// The file is optional: without it WorldData.lakeGroups is absent and every LAKE hex cites the
// lakes layer alone.

import { HEX_COUNT } from '../core/grid.ts';
import type { Reader } from './fetch.ts';
import type { LakeGroup } from './types.ts';

const FILE = 'lake-hexes.json';

interface RawGroup {
  id?: unknown;
  name?: unknown;
  hexes?: unknown;
  sources?: unknown;
}

const isText = (v: unknown): v is string => typeof v === 'string' && v !== '';

/** Why a group cannot be used, or null. A group without sources would cite nothing for its hexes. */
function problemOf(g: RawGroup | null): string | null {
  if (!g || !isText(g.id) || !isText(g.name)) {
    return 'has no id or name';
  }
  if (!Array.isArray(g.sources) || g.sources.length === 0 || !g.sources.every(isText)) {
    return 'has no sources';
  }
  if (!Array.isArray(g.hexes) || !g.hexes.every((h) => Number.isInteger(h) && h >= 0 && h < HEX_COUNT)) {
    return `has a hex outside the ${HEX_COUNT}-hex grid`;
  }
  return null;
}

/** Fetch lake-hexes.json; null (with a warning) when missing or unusable. */
export async function loadLakeGroups(reader: Reader): Promise<LakeGroup[] | null> {
  const raw = await reader.json<{ groups?: unknown }>(FILE);
  if (!raw) {
    return null;
  }

  const groups = Array.isArray(raw.groups) ? (raw.groups as Array<RawGroup | null>) : null;
  if (!groups) {
    return warn(reader, 'lists no groups');
  }

  for (const g of groups) {
    const why = problemOf(g);
    if (why) {
      return warn(reader, `has a group (${String(g?.id)}) that ${why}`);
    }
  }
  return groups as unknown as LakeGroup[]; // every group passed problemOf
}

function warn(reader: Reader, why: string): null {
  const msg = `data: ${FILE} ${why}`;
  reader.warnings.push(msg);
  console.warn(msg);
  return null;
}
